<template>
    <div class="tw-p-2 tw-bg-white tw-w-full tw-border tw-border-solid tw-flex tw-items-center tw-gap-2">
        <router-link :to="{ name: 'pages.admin.dashboard' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <icon icon="solar:home-smile-bold-duotone" /> <span class="tw-text-sm">{{ $t("dashboard.sidebar.dashboard")
            }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <router-link :to="{ name: 'pages.admin.companies' }"
            class="tw-text-gray-400 tw-duration-200 hover:tw-text-gray-700 tw-text-xl tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-sm">{{ $t("dashboard.sidebar.companies")
            }}</span>
        </router-link>
        <span class="tw-text-gray-300">/</span>
        <button disabled class="tw-text-primary-500 tw-text-xl tw-flex tw-items-center tw-gap-2"><span
                class="tw-text-sm">{{
                    $t("common.edit-company") }}</span></button>
    </div>
    <div class="tw-min-h-screen tw-bg-gray-50 tw-p-4">
        <!-- Form Container -->
        <div class="tw-max-w-4xl tw-mx-auto">
            <div class="tw-bg-white tw-rounded-lg tw-shadow-smx tw-border tw-border-gray-200 tw-overflow-hidden">
                <!-- Header -->
                <div class="tw-p-6 tw-border-b tw-border-gray-200 tw-bg-white">
                    <div class="tw-flex tw-items-center tw-gap-3">
                        <div class="tw-w-10 tw-h-10 tw-bg-primary-100 tw-rounded-lg tw-flex tw-items-center tw-justify-center">
                            <icon icon="solar:buildings-2-bold-duotone" class="tw-text-primary-600 tw-text-xl" />
                        </div>
                        <div>
                            <h1 class="tw-text-xl tw-font-semibold tw-text-gray-900">{{ $t("common.edit-company") }}</h1>
                            <p class="tw-text-sm tw-text-gray-600">{{ $t("forms.company.edit-description", "Update company profile information") }}</p>
                        </div>
                    </div>
                </div>

                <!-- Skeleton Loader -->
                <div v-if="loading || formDataLoading" class="tw-p-6">
                    <div class="tw-space-y-6">
                        <!-- Company Name Skeleton -->
                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                            <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <!-- Email Fields Skeleton -->
                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse"></div>
                            <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-36 tw-animate-pulse"></div>
                            <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <!-- Website Skeleton -->
                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse"></div>
                            <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <!-- Two Column Fields Skeleton -->
                        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
                            <div>
                                <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-40 tw-animate-pulse"></div>
                                <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            </div>
                            <div>
                                <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                                <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            </div>
                        </div>

                        <!-- More Two Column Fields -->
                        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
                            <div>
                                <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-32 tw-animate-pulse"></div>
                                <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            </div>
                            <div>
                                <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-36 tw-animate-pulse"></div>
                                <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            </div>
                        </div>

                        <!-- File Upload Skeleton -->
                        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
                            <div>
                                <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28 tw-animate-pulse"></div>
                                <div class="tw-h-24 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            </div>
                            <div>
                                <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-36 tw-animate-pulse"></div>
                                <div class="tw-h-24 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                            </div>
                        </div>

                        <!-- Address and Description Skeleton -->
                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-20 tw-animate-pulse"></div>
                            <div class="tw-h-12 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28 tw-animate-pulse"></div>
                            <div class="tw-h-24 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <!-- Multi-select Fields Skeleton -->
                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-16 tw-animate-pulse"></div>
                            <div class="tw-h-16 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-24 tw-animate-pulse"></div>
                            <div class="tw-h-16 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>

                        <div class="lg:tw-col-span-2">
                            <div class="tw-mb-2 tw-h-4 tw-bg-gray-200 tw-rounded tw-w-28 tw-animate-pulse"></div>
                            <div class="tw-h-16 tw-bg-gray-200 tw-rounded tw-animate-pulse"></div>
                        </div>
                    </div>
                </div>

                <!-- Form -->
                <form v-else @submit.prevent="handleSubmit" class="tw-p-6">
                    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4">
                        <!-- Company Name -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.name"
                                type="text"
                                :label="$t('fields.name')"
                                :placeholder="$t('forms.company.name-placeholder', 'Enter company name')"
                                left-icon="solar:buildings-2-bold-duotone"
                                required
                                :error="errors.name"
                            />
                        </div>

                        <!-- Primary Email -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.primary_email"
                                type="email"
                                :label="$t('forms.company.primary-email', 'Primary Email')"
                                :placeholder="$t('forms.company.primary-email-placeholder', 'Enter primary email')"
                                left-icon="solar:letter-bold-duotone"
                                required
                                :error="errors.primary_email"
                            />
                        </div>

                        <!-- Secondary Email -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.secondary_email"
                                type="email"
                                :label="$t('forms.company.secondary-email', 'Secondary Email')"
                                :placeholder="$t('forms.company.secondary-email-placeholder', 'Enter secondary email (optional)')"
                                left-icon="solar:letter-bold-duotone"
                                :error="errors.secondary_email"
                            />
                        </div>

                        <!-- Website -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.website"
                                type="url"
                                :label="$t('forms.company.website', 'Website')"
                                :placeholder="$t('forms.company.website-placeholder', 'Enter website URL')"
                                left-icon="solar:earth-bold-duotone"
                                :error="errors.website"
                            />
                        </div>

                        <!-- Streaming Platform -->
                        <div>
                            <FormField
                                v-model="form.streaming_platform"
                                type="select"
                                :label="$t('forms.company.streaming-platform', 'Streaming Platform')"
                                :placeholder="$t('forms.company.select-platform', 'Select streaming platform')"
                                left-icon="solar:play-circle-bold-duotone"
                                :options="streamingPlatforms"
                                :error="errors.streaming_platform"
                            />
                        </div>

                        <!-- Country -->
                        <div>
                            <FormVueSelect
                                v-model="form.country_id"
                                :options="countriesOptions"
                                option-value="id"
                                option-label="name"
                                :label="$t('forms.company.country', 'Country')"
                                :placeholder="$t('forms.company.select-country', 'Select country')"
                                left-icon="solar:map-point-bold-duotone"
                                :error="errors.country_id"
                                searchable
                                clearable
                            />
                        </div>

                        <!-- Primary Phone -->
                        <div>
                            <FormField
                                v-model="form.primary_phone"
                                type="tel"
                                :label="$t('forms.company.primary-phone', 'Primary Phone')"
                                :placeholder="$t('forms.company.primary-phone-placeholder', 'Enter primary phone')"
                                left-icon="solar:phone-bold-duotone"
                                :error="errors.primary_phone"
                            />
                        </div>

                        <!-- Secondary Phone -->
                        <div>
                            <FormField
                                v-model="form.secondary_phone"
                                type="tel"
                                :label="$t('forms.company.secondary-phone', 'Secondary Phone')"
                                :placeholder="$t('forms.company.secondary-phone-placeholder', 'Enter secondary phone (optional)')"
                                left-icon="solar:phone-bold-duotone"
                                :error="errors.secondary_phone"
                            />
                        </div>

                        <!-- Logo Upload -->
                        <div>
                            <FormFileUpload
                                v-model="form.logo"
                                :label="$t('forms.company.logo', 'Company Logo')"
                                :placeholder="$t('forms.company.logo-placeholder', 'Upload company logo')"
                                :help="$t('forms.company.logo-help', 'Recommended: 200x200px, PNG/JPG')"
                                left-icon="solar:gallery-bold-duotone"
                                accept="image/*"
                                :error="errors.logo"
                            />
                        </div>

                        <!-- Background Image Upload -->
                        <div>
                            <FormFileUpload
                                v-model="form.background_image"
                                :label="$t('forms.company.background-image', 'Background Image')"
                                :placeholder="$t('forms.company.background-placeholder', 'Upload background image')"
                                :help="$t('forms.company.background-help', 'Recommended: 1920x1080px, PNG/JPG')"
                                left-icon="solar:image-bold-duotone"
                                accept="image/*"
                                :error="errors.background_image"
                            />
                        </div>

                        <!-- Address -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.address"
                                type="text"
                                :label="$t('fields.customer_address')"
                                :placeholder="$t('forms.company.address-placeholder', 'Enter company address')"
                                left-icon="solar:map-point-bold-duotone"
                                :error="errors.address"
                            />
                        </div>

                        <!-- Description -->
                        <div class="lg:tw-col-span-2">
                            <FormField
                                v-model="form.description"
                                type="textarea"
                                :label="$t('forms.company.description', 'Description')"
                                :placeholder="$t('forms.company.description-placeholder', 'Enter company description')"
                                :error="errors.description"
                                rows="4"
                            />
                        </div>

                        <!-- Users -->
                        <div class="lg:tw-col-span-2">
                            <FormMultiSelect
                                v-model="form.users"
                                :options="usersOptions"
                                :label="$t('forms.company.users', 'Users')"
                                :placeholder="$t('forms.company.select-users', 'Select users')"
                                left-icon="solar:users-group-rounded-bold-duotone"
                                :error="errors.users"
                                :help="$t('forms.company.users-help', 'Select users associated with this company')"
                            />
                        </div>

                        <!-- Categories -->
                        <div class="lg:tw-col-span-2">
                            <FormMultiSelect
                                v-model="form.categories"
                                :options="categoriesOptions"
                                :label="$t('forms.company.categories', 'Categories')"
                                :placeholder="$t('forms.company.select-categories', 'Select categories')"
                                left-icon="solar:widget-bold-duotone"
                                :error="errors.categories"
                                :help="`${$t('forms.company.categories-help', 'Select categories for this company')} ${companyData.categories?.length ? `(${companyData.categories.length} total)` : ''}`"
                            />
                        </div>

                        <!-- Certificates -->
                        <div class="lg:tw-col-span-2 tw-mb-6">
                            <FormMultiSelect
                                v-model="form.certificates"
                                :options="certificatesOptions"
                                :label="$t('forms.company.certificates', 'Certificates')"
                                :placeholder="$t('forms.company.select-certificates', 'Select certificates')"
                                left-icon="solar:medal-ribbons-star-bold-duotone"
                                :error="errors.certificates"
                                :help="`${$t('forms.company.certificates-help', 'Select certificates for this company')} ${companyData.certificates?.length ? `(${companyData.certificates.length} total)` : ''}`"
                            />
                        </div>

                        <!-- Social Media Section -->
                        <div class="lg:tw-col-span-2">
                            <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4">{{ $t('forms.company.social-media', 'Social Media') }}</h3>
                            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                                <!-- Facebook -->
                                <FormField
                                    v-model="form.facebook"
                                    type="url"
                                    :label="$t('forms.company.facebook', 'Facebook')"
                                    :placeholder="$t('forms.company.facebook-placeholder', 'Facebook URL')"
                                    left-icon="ri:facebook-fill"
                                    :error="errors.facebook"
                                />

                                <!-- Twitter -->
                                <FormField
                                    v-model="form.twitter"
                                    type="url"
                                    :label="$t('forms.company.x', 'X')"
                                    :placeholder="$t('forms.company.x-placeholder', 'X URL')"
                                    left-icon="hugeicons:new-twitter"
                                    :error="errors.twitter"
                                />

                                <!-- Instagram -->
                                <FormField
                                    v-model="form.instagram"
                                    type="url"
                                    :label="$t('forms.company.instagram', 'Instagram')"
                                    :placeholder="$t('forms.company.instagram-placeholder', 'Instagram URL')"
                                    left-icon="proicons:instagram"
                                    :error="errors.instagram"
                                />

                                <!-- LinkedIn -->
                                <FormField
                                    v-model="form.linkedin"
                                    type="url"
                                    :label="$t('forms.company.linkedin', 'LinkedIn')"
                                    :placeholder="$t('forms.company.linkedin-placeholder', 'LinkedIn URL')"
                                    left-icon="ri:linkedin-fill"
                                    :error="errors.linkedin"
                                />

                                <!-- YouTube -->
                                <FormField
                                    v-model="form.youtube"
                                    type="url"
                                    :label="$t('forms.company.youtube', 'YouTube')"
                                    :placeholder="$t('forms.company.youtube-placeholder', 'YouTube URL')"
                                    left-icon="famicons:logo-youtube"
                                    :error="errors.youtube"
                                />
                            </div>
                        </div>

                        
                    </div>

                    <!-- Form Actions -->
                    <div class="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-8 tw-pt-6 tw-border-t tw-border-gray-200">
                        <a-button 
                            type="button" 
                            intent="simple" 
                            @click="handleCancel"
                            :disabled="isSubmitting"
                        >
                            {{ $t('buttons.cancel') }}
                        </a-button>
                        <a-button 
                            type="submit" 
                            intent="primary" 
                            :loading="isSubmitting"
                            :disabled="isSubmitting"
                        >
                            {{ $t('buttons.update') }}
                        </a-button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormFileUpload from '@/components/form/FormFileUpload.vue';
import FormMultiSelect from '@/components/form/FormMultiSelect.vue';
import FormVueSelect from '@/components/form/FormVueSelect.vue';
import useAlert from '@/composables/useAlert';
import { forms } from '@/api/app';
import companyApi from '@/api/companies';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Loading state
const loading = ref(true);
const formDataLoading = ref(true);

// Company data
const companyData = ref({});

// Form data
const form = ref({
    name: '',
    streaming_platform: '',
    logo: null,
    background_image: null,
    address: '',
    phone: '',
    users: [],
    categories: [],
    certificates: [],
    primary_email: '',
    secondary_email: '',
    website: '',
    country_id: '',
    primary_phone: '',
    secondary_phone: '',
    facebook: '',
    twitter: '',
    instagram: '',
    linkedin: '',
    youtube: ''
});

// Form state
const isSubmitting = ref(false);
const errors = ref({});

// Streaming platform options
const streamingPlatforms = [
    { id: 'youtube', name: 'YouTube' },
    { id: 'twitch', name: 'Twitch' },
    { id: 'facebook', name: 'Facebook Live' },
    { id: 'instagram', name: 'Instagram Live' },
    { id: 'tiktok', name: 'TikTok Live' },
    { id: 'linkedin', name: 'LinkedIn Live' },
    { id: 'other', name: 'Other' }
];

const usersOptions = ref([]);
const categoriesOptions = ref([]);
const certificatesOptions = ref([]);
const countriesOptions = ref([]);

// Fetch company data
const fetchCompanyData = async () => {
    try {
        const response = await companyApi.admin.edit(route.params.id);
        companyData.value = response.data;
        
        // Populate form with company data
        form.value = {
            name: companyData.value.name || '',
            streaming_platform: companyData.value.streaming_platform || '',
            logo: companyData.value.logo || null,
            background_image: companyData.value.background_image || null,
            address: companyData.value.address || '',
            phone: companyData.value.phone || '',
            users: companyData.value.users || [],
            categories: companyData.value.categories || [],
            certificates: companyData.value.certificates || [],
            primary_email: companyData.value.primary_email || '',
            secondary_email: companyData.value.secondary_email || '',
            website: companyData.value.website || '',
            country_id: companyData.value.country_id || '',
            primary_phone: companyData.value.primary_phone || '',
            secondary_phone: companyData.value.secondary_phone || '',
            facebook: companyData.value.facebook || '',
            twitter: companyData.value.twitter || '',
            instagram: companyData.value.instagram || '',
            linkedin: companyData.value.linkedin || '',
            youtube: companyData.value.youtube || '',
            description: companyData.value.description || ''
        };
    } catch (error) {
        console.error('Error fetching company data:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
        router.push({ name: 'pages.admin.companies' });
    } finally {
        loading.value = false;
    }
};

// Form validation
const validateForm = () => {
    errors.value = {};

    if (!form.value.name.trim()) {
        errors.value.name = t('errors.required', { field: t('fields.name') });
    }

    if (!form.value.primary_email.trim()) {
        errors.value.primary_email = t('errors.required', { field: t('forms.company.primary-email') });
    } else if (!isValidEmail(form.value.primary_email)) {
        errors.value.primary_email = t('errors.email');
    }

    if (form.value.secondary_email && !isValidEmail(form.value.secondary_email)) {
        errors.value.secondary_email = t('errors.email');
    }

    if (form.value.primary_phone && !isValidPhone(form.value.primary_phone) && !!undefined) {
        errors.value.primary_phone = t('errors.phone');
    }

    if (form.value.secondary_phone && !isValidPhone(form.value.secondary_phone) && !!undefined) {
        errors.value.secondary_phone = t('errors.phone');
    }

    if (form.value.website && !isValidUrl(form.value.website)) {
        errors.value.website = t('errors.url');
    }

    if (form.value.facebook && !isValidUrl(form.value.facebook)) {
        errors.value.facebook = t('errors.url');
    }

    if (form.value.twitter && !isValidUrl(form.value.twitter)) {
        errors.value.twitter = t('errors.url');
    }

    if (form.value.instagram && !isValidUrl(form.value.instagram)) {
        errors.value.instagram = t('errors.url');
    }

    if (form.value.linkedin && !isValidUrl(form.value.linkedin)) {
        errors.value.linkedin = t('errors.url');
    }

    if (form.value.youtube && !isValidUrl(form.value.youtube)) {
        errors.value.youtube = t('errors.url');
    }

    return Object.keys(errors.value).length === 0;
};

const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

const isValidPhone = (phone) => {
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-()]/g, ''));
};

// Form submission
const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;

    try {
        // Create FormData for file uploads
        const formData = new FormData();
        
        // Add text fields
        formData.append('name', form.value.name);
        formData.append('primary_email', form.value.primary_email);
        formData.append('secondary_email', form.value.secondary_email || '');
        formData.append('website', form.value.website || '');
        formData.append('streaming_platform', form.value.streaming_platform);
        formData.append('country_id', form.value.country_id || '');
        formData.append('primary_phone', form.value.primary_phone || '');
        formData.append('secondary_phone', form.value.secondary_phone || '');
        formData.append('address', form.value.address || '');
        formData.append('description', form.value.description || '');
        formData.append('facebook', form.value.facebook || '');
        formData.append('twitter', form.value.twitter || '');
        formData.append('instagram', form.value.instagram || '');
        formData.append('linkedin', form.value.linkedin || '');
        formData.append('youtube', form.value.youtube || '');

        // Add multiple select fields (IDs only)
        if (form.value.users.length > 0) {
            const userIds = form.value.users.map(user => user.id);
            formData.append('user_ids', JSON.stringify(userIds));
        }
        
        if (form.value.categories.length > 0) {
            const categoryIds = form.value.categories.map(category => category.id);
            formData.append('category_ids', JSON.stringify(categoryIds));
        }
        
        if (form.value.certificates.length > 0) {
            const certificateIds = form.value.certificates.map(certificate => certificate.id);
            formData.append('certificate_ids', JSON.stringify(certificateIds));
        }

        // Add files if selected (only if new files are uploaded)
        if (form.value.logo instanceof File) {
            formData.append('logo', form.value.logo);
        } else if (typeof form.value.logo === 'string' && form.value.logo) {
            // Keep existing logo URL
            formData.append('logo_url', form.value.logo);
        }
        
        if (form.value.background_image instanceof File) {
            formData.append('background_image', form.value.background_image);
        } else if (typeof form.value.background_image === 'string' && form.value.background_image) {
            // Keep existing background image URL
            formData.append('background_image_url', form.value.background_image);
        }

        await companyApi.admin.update(route.params.id, formData);
        
        useAlert(t('messages.updated-successfully', { field: t('fields.company') }), 'success');
        router.push({ name: 'pages.admin.companies' });

    } catch (error) {
        console.error('Error updating company:', error);
        
        if (error.response?.status === 422) {
            // Handle validation errors from server
            const serverErrors = error.response.data.errors;
            Object.keys(serverErrors).forEach(key => {
                errors.value[key] = serverErrors[key][0];
            });
        } else {
            useAlert(t('errors.something-wrong-happened'), 'danger');
        }
    } finally {
        isSubmitting.value = false;
    }
};

const handleCancel = () => {
    router.push({ name: 'pages.admin.companies' });
};

const getFormData = async () => {
    try {
        const { data } = await forms.users(['exhibitor']);
        usersOptions.value = data;

        const { data: categories } = await forms.categories('company');
        categoriesOptions.value = categories;

        const { data: certificates } = await forms.certificates('company');
        certificatesOptions.value = certificates;

        const { data: countries } = await forms.countries();
        countriesOptions.value = countries;
    } catch (error) {
        console.error('Error fetching form data:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        formDataLoading.value = false;
    }
}

onMounted(async () => {
    // Load both company data and form data in parallel
    getFormData(),
    await Promise.all([
        fetchCompanyData()
    ]);
})
</script>

<style scoped>
/* Custom styles for the form */
.form-section {
    @apply tw-space-y-6;
}

.form-section-title {
    @apply tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-4;
}
</style>
